/**
 * useStepUp — one place for "ask for the code, then do the thing".
 *
 * plan-v2 §4 asks for the confirmation and the second factor to be **one act**: the code is
 * typed in the dialog that confirms the change, not collected beforehand on some other screen.
 * That is not presentation. A dialog that merely says "are you sure?" can be clicked through
 * by whoever has the session; one that demands a code cannot be completed by somebody who has
 * only taken over a browser.
 *
 * Two entry points, because two situations arise:
 *
 *  · `confirmAndRun` — the caller KNOWS the change is dangerous (switching a protection off,
 *    disabling 2FA) and wants the dialog up front, with the consequence spelled out, every
 *    time. Never skipped on the strength of a recent sign-in: a destructive change deserves a
 *    deliberate act, not the residue of one.
 *  · `runGuarded` — the caller does not know. It tries, and only if the server answers
 *    STEP_UP_REQUIRED does the dialog appear. Nothing has happened at that point; a 403 is the
 *    server declining to act.
 *
 * Both re-run the SAME request with the ticket attached, so a guarded action needs no second
 * code path and cannot drift from its unguarded self.
 */

import { ref, readonly } from 'vue'
import { apiPost, extractStepUp, extractApiError, type StepUpChallenge } from '@/api/client'
import type { TwoFactorAction } from '@/types/jispman'

export interface StepUpPrompt {
  /** Which gate this is for. The ticket is bound to it server-side. */
  action: TwoFactorAction
  /** "Turn off two-factor for withdrawals?" — the question, in the user's words. */
  title: string
  /** What will be true afterwards. For anything destructive, stated in full. */
  consequence?: string
  /** Who will be emailed about this, when somebody will be. */
  notify?: string
  /** The button's words. "Turn it off", not "OK". */
  confirmLabel?: string
  /** Styles the confirm button as destructive and keeps the consequence prominent. */
  danger?: boolean
  /**
   * Ask for a code even when the session could satisfy the gate on its own. True for anything
   * destructive — see the note above.
   */
  requireCode?: boolean
}

type Resolver = (ticket: string | null) => void

/**
 * Module-level so one dialog instance, mounted once in the shell, serves every caller. Two
 * dialogs racing to be the modal is a worse bug than the duplication it would avoid.
 */
const open = ref(false)
const prompt = ref<StepUpPrompt | null>(null)
const busy = ref(false)
const error = ref<string | null>(null)
const notEnrolled = ref(false)
let resolve: Resolver | null = null

function close(ticket: string | null) {
  open.value = false
  error.value = null
  busy.value = false
  const r = resolve
  resolve = null
  prompt.value = null
  r?.(ticket)
}

export function useStepUp() {
  /** Show the dialog and resolve with a ticket, or null if the user backed out. */
  function ask(p: StepUpPrompt, challenge?: StepUpChallenge): Promise<string | null> {
    prompt.value = p
    notEnrolled.value = challenge?.reason === 'not_enrolled'
    error.value = null
    open.value = true
    return new Promise<string | null>(r => { resolve = r })
  }

  /** The dialog's submit: exchange the code for a one-use ticket. */
  async function submitCode(code: string): Promise<void> {
    if (!prompt.value) return
    busy.value = true
    error.value = null
    try {
      const res = await apiPost<{ ticket: string; usedRecoveryCode: boolean }>('/2fa/step-up', {
        code: code.trim(),
        action: prompt.value.action,
      })
      close(res.ticket)
    } catch (err) {
      // Stays open on a wrong code: closing would lose the half-filled form behind it, and
      // mistyping six digits is the single commonest thing that happens here.
      error.value = extractApiError(err, 'That code was not accepted.')
      busy.value = false
    }
  }

  /** The dialog's confirm when no code is needed — the short "are you sure" for enabling. */
  function confirmWithoutCode(): void {
    close('')
  }

  function cancel(): void {
    close(null)
  }

  /**
   * Confirm first, then act. Returns the action's result, or null if the user cancelled.
   *
   * `run` receives the ticket and must pass it to `apiPost`/`apiPut` as `stepUpTicket`. An
   * empty string means "confirmed, no code was needed" — harmless to send, since the server
   * ignores an empty header and falls back to the session's own claims.
   */
  async function confirmAndRun<T>(
    p: StepUpPrompt,
    run: (ticket: string) => Promise<T>,
  ): Promise<T | null> {
    const ticket = await ask({ requireCode: true, ...p })
    if (ticket === null) return null
    return run(ticket)
  }

  /**
   * Try, and only ask if the server says it must.
   *
   * The first attempt carries no ticket. If it is refused for want of a step-up, the dialog
   * opens and the same call is made again with the ticket. Any other failure is re-thrown
   * untouched, so ordinary error handling in the caller still sees what it expects.
   */
  async function runGuarded<T>(
    p: StepUpPrompt,
    run: (ticket?: string) => Promise<T>,
  ): Promise<T | null> {
    try {
      return await run()
    } catch (err) {
      const challenge = extractStepUp(err)
      if (!challenge) throw err

      const ticket = await ask(p, challenge)
      if (ticket === null) return null
      return run(ticket)
    }
  }

  return {
    // State for the dialog component.
    open: readonly(open),
    prompt: readonly(prompt),
    busy: readonly(busy),
    error: readonly(error),
    notEnrolled: readonly(notEnrolled),
    // Dialog actions.
    submitCode,
    confirmWithoutCode,
    cancel,
    // Caller actions.
    confirmAndRun,
    runGuarded,
  }
}
